import Divider from '@cobliteam/ui/components/Divider';
import Skeleton from '@cobliteam/ui/components/Skeleton';
import Typography from '@cobliteam/ui/components/Typography';
import { cn } from '@cobliteam/ui/utils';
import type React from 'react';

const Sizes = {
  full: '!w-full',
  xs: '!w-[320px]',
  sm: '!w-[410px]',
  md: '!w-[600px]',
  lg: '!w-[800px]',
  xl: '!w-[1000px]',
} as const;

interface CustomPopUpProps {
  size?: keyof typeof Sizes;
}

interface ChildrenProps extends React.PropsWithChildren {
  className?: string;
}

const CustomPopUp = ({ size = 'md', children, className, ...props }: ChildrenProps & CustomPopUpProps) => {
  const sizeClass = Sizes[size];
  return (
    <div
      className={cn('[&_p]:!m-0 pointer-events-auto relative flex flex-col overflow-hidden rounded-lg border border-gray-6 bg-white shadow-md', sizeClass, className)}
      {...props}
    >
      {children}
    </div>
  );
};

const CustomPopUpHeader = ({ children, className, ...props }: ChildrenProps) => {
  return (
    <div className={cn('flex items-center justify-between gap-5 bg-gray-lightest p-2.5', className)} {...props}>
      {children}
    </div>
  );
};

const CustomPopUpHeaderMain = ({ children, className, ...props }: ChildrenProps) => {
  return (
    <div className='flex items-center gap-2' {...props}>
      {children}
    </div>
  );
};

const CustomPopUpTitle = ({ children, className, ...props }: ChildrenProps) => {
  return (
    <Typography variant='text-md-medium' className={cn('!text-black !font-medium leading-none', className)} {...props}>
      {children}
    </Typography>
  );
};

const CustomPopUpContent = ({ children, className, ...props }: ChildrenProps) => {
  return (
    <div className={cn('flex flex-col gap-y-2.5 p-2.5', className)} {...props}>
      {children}
    </div>
  );
};

const CustomPopUpSkeleton = ({ className }: ChildrenProps) => {
  return (
    <Skeleton className={cn('w-full bg-gray-7', className)} height={20}>
      <rect x='0' y='0' width='100%' height='100%' />
    </Skeleton>
  );
};

const CustomPopUpFooter = ({ children, className }: ChildrenProps) => {
  return (
    <div className={className}>
      <Divider />
      <div className='flex justify-center gap-2.5 p-2.5'>{children}</div>
    </div>
  );
};

const CustomPopUpIconWrapper = ({ children, className, ...props }: ChildrenProps) => {
  return (
    <div className={cn('flex aspect-square size-7 rounded-lg border-2 border-white shadow', className)} {...props}>
      <div className='m-auto'>{children}</div>
    </div>
  );
};

export { CustomPopUp, CustomPopUpContent, CustomPopUpFooter, CustomPopUpHeader, CustomPopUpHeaderMain, CustomPopUpIconWrapper, CustomPopUpSkeleton, CustomPopUpTitle };
